import { Group, Tooltip } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { Palette, User, Volume2 } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { BackgroundCaps } from "../../../api/generated/model";
import { VideoBackgroundQuote, VideoBackgroundRequest } from "../../../api/generated/zod";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ModelPicker } from "../../../ui/ModelPicker";
import { Pill } from "../../../ui/Pill";
import {
  Errors,
  ROUTE,
  StartGroup,
  VideoSlot,
  defaultModel,
  rowsOf,
  type ToolFormProps,
} from "./form-parts";
import { useToolVideo } from "./use-tool-video";

/** Live's toggle chips' words and tips (`edit-background-form/constants.ts`, `EDIT_BACKGROUND_TOOLTIP`). */
const PRESERVE = {
  on: { label: "Preserve Audio", tip: "Audio will be preserved in the output video." },
  off: { label: "Do Not Preserve Audio", tip: "Audio will be removed from the output video." },
};
const SUBJECT = {
  on: { label: "Subject is a Person", tip: "Set to False if the subject is not a person." },
  off: { label: "Subject is Not a Person", tip: "Set to True if the subject is a person." },
};

/** A model's first background color: its default, else its first, else none. */
function colorDefault(caps: BackgroundCaps | null | undefined): string | null {
  const colors = caps?.background_colors;
  if (colors === null || colors === undefined) return null;
  return colors.values.includes(colors.default) ? colors.default : (colors.values[0] ?? null);
}

/** Live's toggle chip: one icon, a word for each state, soft primary while on. */
function ToggleChip(props: {
  icon: ReactNode;
  on: boolean;
  words: typeof PRESERVE;
  onChange: (on: boolean) => void;
}) {
  const { icon, on, words, onChange } = props;
  const now = on ? words.on : words.off;
  return (
    <Tooltip label={now.tip} withArrow>
      <Pill
        label={now.label}
        icon={icon}
        active={on}
        aria-pressed={on}
        onClick={() => onChange(!on)}
      />
    </Tooltip>
  );
}

/**
 * Live's Edit Background form (#213, `edit-background-form`, captures 09 and L08 to L08d,
 * G7-video-tools): the "Video" slot and no prompt; the model; the model's own settings: Veed's
 * "Subject is a Person", Bria's background color (Transparent first) and "Preserve Audio", each
 * on by default; the price (free, quoted at 0) and the start. Only the model's own settings are
 * sent, as the route refuses another's; a color the next model lacks falls to its first.
 */
export function BackgroundForm({ catalog, above, overlay, onStarted, inline }: ToolFormProps) {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const models = rowsOf(catalog, "video.background");
  const [modelId, setModelId] = useState(() => defaultModel(models).id);
  const model = models.find((each) => each.id === modelId) ?? defaultModel(models);
  const caps = model.modes["video.background"]?.background;
  const colors = caps?.background_colors?.values ?? [];
  const [kept, setColor] = useState<string | null>(null);
  // The color shown while the model offers it, else the model's first (live's model defaults).
  const color = kept !== null && colors.includes(kept) ? kept : colorDefault(caps);
  const [preserveAudio, setPreserveAudio] = useState(true);
  const [subjectIsPerson, setSubjectIsPerson] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const tool = useToolVideo(teamId, brandId);
  const fields = {
    type: "video.background" as const,
    model: model.id,
    ...(tool.ref === null ? {} : { source_video: tool.ref }),
    ...(caps?.subject_is_person === true ? { subject_is_person: subjectIsPerson } : {}),
    ...(caps?.preserve_audio === true ? { preserve_audio: preserveAudio } : {}),
    ...(color === null ? {} : { background_color: color }),
  };
  const quote = VideoBackgroundQuote.safeParse(fields);
  const request = VideoBackgroundRequest.safeParse(fields);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      header={<VideoSlot tool={tool} />}
      summary={<span>Edit Background</span>}
      above={above}
      overlay={overlay}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={8} wrap="nowrap">
            <ModelPicker models={models} value={model.id} onChange={setModelId} />
            {color === null ? null : (
              <Choice
                setting="Background"
                icon={<Palette size={20} strokeWidth={1.5} />}
                chevron={false}
                width={150}
                tip="Defines the replacement background color."
                value={color}
                options={colors.map((each) => ({ value: each, label: each }))}
                onChange={setColor}
              />
            )}
            {caps?.preserve_audio === true ? (
              <ToggleChip
                icon={<Volume2 size={20} strokeWidth={1.5} />}
                on={preserveAudio}
                words={PRESERVE}
                onChange={setPreserveAudio}
              />
            ) : null}
            {caps?.subject_is_person === true ? (
              <ToggleChip
                icon={<User size={20} strokeWidth={1.5} />}
                on={subjectIsPerson}
                words={SUBJECT}
                onChange={setSubjectIsPerson}
              />
            ) : null}
          </Group>
          <StartGroup job={job} notes={model.modes["video.background"]?.notes ?? []} />
        </Group>
      }
    >
      <Errors job={job} uploads={[tool.upload]} />
      {tool.dialog}
    </GlassPanel>
  );
}
